import { createContext } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import { getWorkspaceBySlug, logoutSession } from './api';

const AuthContext = createContext(null);

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'unplug-app.com';
const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';
const MARKETING_URL = import.meta.env.VITE_MARKETING_URL || 'https://unplug-app.com';

// Reserved subdomains that are not valid workspace slugs
const RESERVED_SUBDOMAINS = [
  'api', 'www', 'admin', 'mail', 'ftp', 'smtp', 'pop', 'imap',
  'app', 'dashboard', 'login', 'auth', 'oauth', 'signup', 'register',
  'help', 'support', 'docs', 'status', 'blog', 'cdn', 'static',
  'super-admin', 'superadmin', 'root', 'system', 'null', 'undefined'
];

// Extract workspace slug from subdomain
function getWorkspaceSlug() {
  const hostname = window.location.hostname;

  // Development: localhost doesn't have subdomains
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // In dev, use query param or localStorage fallback
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('workspace') || localStorage.getItem('devWorkspaceSlug') || null;
  }

  // Production: extract subdomain from hostname
  if (hostname.endsWith(`.${APP_DOMAIN}`)) {
    const subdomain = hostname.replace(`.${APP_DOMAIN}`, '');
    // Check it's not reserved and is valid format
    if (!RESERVED_SUBDOMAINS.includes(subdomain) && /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/.test(subdomain)) {
      return subdomain;
    }
    return null; // Invalid subdomain
  }

  // Root domain (marketing site) or unrecognized domain
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [workspaceSlug, setWorkspaceSlug] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // 'invalid_subdomain', 'workspace_not_found', 'subscription_expired'

  // Check if workspace has access to a specific feature
  function hasFeature(featureKey) {
    return features.includes(featureKey);
  }

  useEffect(() => {
    initializeWorkspace();
  }, []);

  async function initializeWorkspace() {
    console.log('[Auth] Initializing workspace...');
    try {
      // Step 1: Get workspace slug from subdomain
      const slug = getWorkspaceSlug();
      console.log('[Auth] Slug from subdomain:', slug);
      setWorkspaceSlug(slug);

      // If no slug and we're on a subdomain, it's invalid
      const hostname = window.location.hostname;
      const isSubdomain = hostname.endsWith(`.${APP_DOMAIN}`) && hostname !== APP_DOMAIN && hostname !== `www.${APP_DOMAIN}`;

      if (isSubdomain && !slug) {
        setError('invalid_subdomain');
        setLoading(false);
        return;
      }

      // If no slug (root domain or localhost without workspace), just check if we have a session
      if (!slug) {
        setLoading(false);
        return;
      }

      // Step 2: Validate workspace exists via API
      console.log('[Auth] Fetching workspace data for slug:', slug);
      const workspaceData = await getWorkspaceBySlug(slug);
      console.log('[Auth] Workspace data:', workspaceData);

      if (!workspaceData || !workspaceData.workspace) {
        console.error('[Auth] Workspace not found');
        setError('workspace_not_found');
        setLoading(false);
        return;
      }

      // Step 3: Check if subscription is valid
      if (!workspaceData.subscription_valid) {
        setError('subscription_expired');
        setWorkspace(workspaceData.workspace);
        setLoading(false);
        return;
      }

      // Store workspace info from slug lookup
      const workspaceFromSlug = workspaceData.workspace;

      // Step 4: Check for existing session
      const sessionId = localStorage.getItem('sessionId');
      console.log('[Auth] Session ID from localStorage:', sessionId ? sessionId.substring(0, 8) + '...' : 'none');

      if (!sessionId) {
        // No session but valid workspace - show login
        console.log('[Auth] No session - showing login');
        setWorkspace(workspaceFromSlug);
        setLoading(false);
        return;
      }

      // Step 5: Validate session (use /v3/ to bypass stale cached /api/ responses)
      console.log('[Auth] Validating session...');
      const response = await fetch(`${API_URL}/v3/auth/session?_t=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ sessionId, workspaceId: workspaceFromSlug.id })
      });

      console.log('[Auth] Session validation response status:', response.status);

      if (response.ok) {
        try {
          const text = await response.text();
          const data = text ? JSON.parse(text) : {};
          console.log('[Auth] Session valid, user:', data.user?.name);
          setUser(data.user);
          setWorkspace(data.workspace);
          setSubscription(data.subscription || null);
          setFeatures(data.features || []);
        } catch (parseError) {
          console.error('[Auth] Failed to parse session response:', parseError);
          localStorage.removeItem('sessionId');
          setWorkspace(workspaceFromSlug);
        }
      } else {
        // Invalid session - clear it but keep workspace
        console.log('[Auth] Session invalid, clearing and showing login');
        localStorage.removeItem('sessionId');
        setWorkspace(workspaceFromSlug);
      }
    } catch (err) {
      console.error('Workspace initialization failed:', err);
      setError('initialization_failed');
    } finally {
      setLoading(false);
    }
  }

  // Login with just session ID (workspace derived from subdomain)
  function login(sessionId) {
    localStorage.setItem('sessionId', sessionId);
    // Re-initialize to validate session
    setLoading(true);
    initializeWorkspace();
  }

  function logout() {
    // Invalidate the server-side session BEFORE dropping the token
    // (fire-and-forget - local logout must succeed even if the API is down)
    logoutSession().catch(() => {});
    localStorage.removeItem('sessionId');
    setUser(null);
    setSubscription(null);
    setFeatures([]);
    // Keep workspace (from subdomain) but clear user
  }

  // Re-validate session and refresh all state from server
  async function refreshSession() {
    await initializeWorkspace();
  }

  // Optimistically update user state without a full session refresh
  function updateUser(updates) {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  }

  // Redirect to marketing site (for invalid workspace scenarios)
  function redirectToMarketing() {
    window.location.href = MARKETING_URL;
  }

  return (
    <AuthContext.Provider value={{
      user,
      workspace,
      workspaceSlug,
      subscription,
      features,
      hasFeature,
      loading,
      error,
      login,
      logout,
      refreshSession,
      updateUser,
      redirectToMarketing
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
